import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { CircleNotch } from 'phosphor-react';
import Layout from '../../../components/Learner/Layout';
import Header from './Header';
import OverallPanel from './OverallPanel';
import LevelPanel from './LevelPanel';
import { getCourseTree } from '../../../services/learningApi';
import { courseTreeAtom, courseTreeBaselineAtom } from '../../../recoil/atoms';
import { getFirstSubcourseByOrder, getSubcourseLevelCode } from '../../../utils/courseTreeUtils';
import { LANGUAGE_CONFIG, ROUTE_TO_LANG } from './languageConfig';

const LanguageLearningPage = () => {
  const { pathname } = useLocation();
  const config = useMemo(() => {
    const langKey = ROUTE_TO_LANG[pathname] || 'de';
    return LANGUAGE_CONFIG[langKey];
  }, [pathname]);
  const { langCode, fromPage } = config;
  const logPrefix = `LanguageLearningPage(${langCode})`;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize selectedLevel - tunggu courseTree jika belum ada subcourse-id di URL
  const getInitialLevel = () => {
    const urlSubcourseId = searchParams.get('subcourse-id');
    if (urlSubcourseId) {
      return null;
    }

    try {
      const lastOpenedUrl = localStorage.getItem('lastOpenedUrl');
      if (lastOpenedUrl) {
        const urlObj = new URL(lastOpenedUrl, window.location.origin);
        const subcourseId = urlObj.searchParams.get('subcourse-id');
        if (subcourseId) {
          return null;
        }
      }
    } catch (e) {
      // Ignore
    }

    return null;
  };

  const [selectedLevel, setSelectedLevel] = useState(getInitialLevel);
  const [courseTree, setCourseTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const setCourseTreeAtom = useSetRecoilState(courseTreeAtom);
  const setCourseTreeBaseline = useSetRecoilState(courseTreeBaselineAtom);

  const findSubcourseByLevel = (tree, level) => {
    if (!tree || !level) return null;

    let courses = [];
    if (Array.isArray(tree)) {
      courses = tree;
    } else if (tree && Array.isArray(tree.courses)) {
      courses = tree.courses;
    } else if (tree && typeof tree === 'object') {
      const possibleKeys = ['courses', 'data', 'courseTree', 'tree'];
      for (const key of possibleKeys) {
        if (Array.isArray(tree[key])) {
          courses = tree[key];
          break;
        }
      }
      if (courses.length === 0) {
        const values = Object.values(tree);
        const allArrays = values.filter(v => Array.isArray(v));
        if (allArrays.length > 0) {
          courses = allArrays.flat();
        }
      }
    }

    if (courses.length === 0) {
      return null;
    }

    for (const course of courses) {
      if (!course.subcourses || !Array.isArray(course.subcourses)) {
        continue;
      }

      for (const subcourse of course.subcourses) {
        const subcourseCode = subcourse['subcourse-code'] || subcourse.subcourseCode || subcourse.code;
        const subcourseName = subcourse.name || subcourse.title;
        const subcourseLevel = subcourse.level || subcourse.levelCode;

        const matchValue = subcourseCode || subcourseName || subcourseLevel;
        if (matchValue && matchValue.toString().toLowerCase() === level.toLowerCase()) {
          return {
            ...subcourse,
            id: subcourse._id || subcourse.id
          };
        }
      }
    }

    return null;
  };

  const findLevelBySubcourseId = (tree, subcourseId) => {
    if (!tree || !subcourseId) {
      console.log('🔍 findLevelBySubcourseId: Missing params', { tree: !!tree, subcourseId });
      return null;
    }

    let courses = [];
    if (Array.isArray(tree)) {
      courses = tree;
    } else if (tree && Array.isArray(tree.courses)) {
      courses = tree.courses;
    } else if (tree && typeof tree === 'object') {
      const possibleKeys = ['courses', 'data', 'courseTree', 'tree'];
      for (const key of possibleKeys) {
        if (Array.isArray(tree[key])) {
          courses = tree[key];
          break;
        }
      }
      if (courses.length === 0) {
        const values = Object.values(tree);
        const allArrays = values.filter(v => Array.isArray(v));
        if (allArrays.length > 0) {
          courses = allArrays.flat();
        }
      }
    }

    for (const course of courses) {
      if (!course.subcourses || !Array.isArray(course.subcourses)) {
        continue;
      }

      for (const subcourse of course.subcourses) {
        const subcourseIdFromTree = subcourse.id || subcourse._id;
        if (subcourseIdFromTree && String(subcourseIdFromTree) === String(subcourseId)) {
          const foundLevel = subcourse['subcourse-code'] || subcourse.subcourseCode || subcourse.code || subcourse.level || subcourse.levelCode;
          console.log('✅ findLevelBySubcourseId: Found level:', foundLevel);
          return foundLevel;
        }
      }
    }

    console.log('❌ findLevelBySubcourseId: Subcourse not found');
    return null;
  };

  const hasRestoredLevelRef = useRef(false);
  const userChangedLevelRef = useRef(false);
  const prevPathnameRef = useRef(pathname);
  const prevLangCodeRef = useRef(langCode);

  const updateUrlParams = useCallback((params) => {
    const newParams = new URLSearchParams(searchParams);

    Object.keys(params).forEach((key) => {
      if (
        params[key] === null ||
        params[key] === undefined ||
        params[key] === ""
      ) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(params[key]));
      }
    });

    const newSearch = newParams.toString();
    const newHash = newSearch
      ? `#${window.location.pathname}?${newSearch}`
      : `#${window.location.pathname}`;

    if (window.location.hash !== newHash) {
      window.location.hash = newHash;
    }

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const resolveSubcourseIdFromUrl = () => {
    const currentSubcourseId = searchParams.get('subcourse-id');
    if (currentSubcourseId) return currentSubcourseId;

    try {
      const lastOpenedUrl = localStorage.getItem('lastOpenedUrl');
      if (!lastOpenedUrl) return null;

      const urlObj = new URL(lastOpenedUrl, window.location.origin);
      if (urlObj.pathname !== pathname) {
        console.log('📖', logPrefix, ': Ignoring lastOpenedUrl from different route:', urlObj.pathname);
        return null;
      }

      return urlObj.searchParams.get('subcourse-id');
    } catch (e) {
      return null;
    }
  };

  const applyFirstSubcourseFallback = () => {
    const firstSubcourse = getFirstSubcourseByOrder(courseTree);
    hasRestoredLevelRef.current = true;

    if (!firstSubcourse) {
      console.warn('⚠️', logPrefix, ': No subcourses in courseTree');
      setSelectedLevel(null);
      updateUrlParams({ 'subcourse-id': null, 'lesson-id': null, 'skill-id': null });
      return;
    }

    const levelCode = getSubcourseLevelCode(firstSubcourse);
    const subcourseId = firstSubcourse.id || firstSubcourse._id;
    console.log('✅', logPrefix, ': Redirecting to first subcourse by order:', { levelCode, subcourseId, order: firstSubcourse.order });
    setSelectedLevel(levelCode);
    updateUrlParams({
      'subcourse-id': subcourseId,
      'lesson-id': null,
      'skill-id': null,
    });
  };

  // Reset state synchronously before restore effect runs (prevents stale EN/A1 on JA switch)
  useLayoutEffect(() => {
    const prevPath = prevPathnameRef.current;
    if (prevPath !== pathname) {
      const prevLangKey = ROUTE_TO_LANG[prevPath];
      const currLangKey = ROUTE_TO_LANG[pathname];
      if (prevLangKey && currLangKey && prevLangKey !== currLangKey) {
        console.log('🌐', logPrefix, ': Language switched, resetting for first subcourse by order');
        hasRestoredLevelRef.current = false;
        userChangedLevelRef.current = false;
        setCourseTree(null);
        setSelectedLevel(null);
        updateUrlParams({
          'subcourse-id': null,
          'lesson-id': null,
          'skill-id': null,
        });
      }
      prevPathnameRef.current = pathname;
    }

    if (prevLangCodeRef.current !== langCode) {
      prevLangCodeRef.current = langCode;
      hasRestoredLevelRef.current = false;
    }
  }, [pathname, langCode, logPrefix, updateUrlParams]);

  useEffect(() => {
    if (!courseTree) {
      console.log('⏳', logPrefix, ': Waiting for courseTree to restore subcourse');
      return;
    }

    if (userChangedLevelRef.current) {
      console.log('👤', logPrefix, ': User manually changed level, skipping restore');
      userChangedLevelRef.current = false;
      return;
    }

    if (hasRestoredLevelRef.current) {
      console.log('✅', logPrefix, ': Already restored level, skipping');
      return;
    }

    let urlSubcourseId = resolveSubcourseIdFromUrl();

    if (urlSubcourseId) {
      const levelFromUrl = findLevelBySubcourseId(courseTree, urlSubcourseId);
      if (levelFromUrl) {
        hasRestoredLevelRef.current = true;
        setSelectedLevel(levelFromUrl);
        if (!searchParams.get('subcourse-id')) {
          updateUrlParams({
            'subcourse-id': urlSubcourseId,
            'lesson-id': null,
            'skill-id': null,
          });
        }
        console.log('✅', logPrefix, ': Restored level from URL params:', levelFromUrl);
        return;
      }

      console.warn('⚠️', logPrefix, ': URL subcourse-id not found in courseTree, using first subcourse by order');
      applyFirstSubcourseFallback();
      return;
    }

    if (!hasRestoredLevelRef.current) {
      try {
        const prevSubcourseStr = localStorage.getItem('prev-subcourse');
        if (!prevSubcourseStr) {
          console.log('📖', logPrefix, ': No prev-subcourse found, using first subcourse by order');
          applyFirstSubcourseFallback();
          return;
        }

        const prevSubcourse = JSON.parse(prevSubcourseStr);
        console.log('📦', logPrefix, ': Found prev-subcourse:', prevSubcourse);

        const age = Date.now() - (prevSubcourse.timestamp || 0);
        console.log('⏰', logPrefix, ': prev-subcourse age:', Math.round(age / 1000), 'seconds');

        if (age > 10 * 60 * 1000) {
          localStorage.removeItem('prev-subcourse');
          console.log('⏰', logPrefix, ': prev-subcourse expired, using first subcourse by order');
          applyFirstSubcourseFallback();
          return;
        }

        const prevSubcourseId = prevSubcourse['subcourse-id'];
        if (prevSubcourseId) {
          const levelFromPrev = findLevelBySubcourseId(courseTree, prevSubcourseId);
          if (levelFromPrev) {
            hasRestoredLevelRef.current = true;
            setSelectedLevel(levelFromPrev);
            updateUrlParams({
              'subcourse-id': prevSubcourseId,
              'lesson-id': prevSubcourse['lesson-id'] || null,
              'skill-id': prevSubcourse['skill-id'] || null,
            });
            console.log('✅', logPrefix, ': Restored from prev-subcourse id:', levelFromPrev);
            return;
          }

          console.warn('⚠️', logPrefix, ': prev-subcourse-id not in this courseTree, using first subcourse by order:', prevSubcourseId);
          localStorage.removeItem('prev-subcourse');
          applyFirstSubcourseFallback();
          return;
        }

        if (prevSubcourse['subcourse-code']) {
          const subcourseCode = prevSubcourse['subcourse-code'];
          const subcourseExists = findSubcourseByLevel(courseTree, subcourseCode);
          if (!subcourseExists) {
            console.warn('⚠️', logPrefix, ': subcourse-code from prev-subcourse not in this courseTree, using first subcourse by order:', subcourseCode);
            localStorage.removeItem('prev-subcourse');
            applyFirstSubcourseFallback();
            return;
          }
          hasRestoredLevelRef.current = true;
          setSelectedLevel(subcourseCode);
          const subcourseId = subcourseExists.id || subcourseExists._id;
          updateUrlParams({
            'subcourse-id': subcourseId,
            'lesson-id': prevSubcourse['lesson-id'] || null,
            'skill-id': prevSubcourse['skill-id'] || null,
          });
          console.log('✅', logPrefix, ': SUCCESS - Switched to subcourse:', subcourseCode, 'from prev-subcourse');
        } else {
          console.log('❌', logPrefix, ': No subcourse-code in prev-subcourse, using first subcourse by order');
          applyFirstSubcourseFallback();
        }
      } catch (e) {
        console.error('❌', logPrefix, ': Error restoring prev-subcourse:', e);
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to restore prev-subcourse from localStorage:', e);
        }
        applyFirstSubcourseFallback();
      }
    }
  }, [courseTree, searchParams, logPrefix, langCode, pathname]); // selectedLevel omitted to prevent infinite loop

  // Safeguard: selectedLevel must exist in current courseTree (fixes stale A1 on Japanese page)
  useEffect(() => {
    if (!courseTree || loading) return;
    if (!selectedLevel || selectedLevel === 'Overall') return;
    if (userChangedLevelRef.current) return;

    if (!findSubcourseByLevel(courseTree, selectedLevel)) {
      console.warn('⚠️', logPrefix, ': selectedLevel invalid for courseTree, using first subcourse by order:', selectedLevel);
      hasRestoredLevelRef.current = false;
      applyFirstSubcourseFallback();
    }
  }, [courseTree, selectedLevel, loading, langCode, logPrefix, updateUrlParams]);

  const handleLevelChange = (newLevel) => {
    console.log('👤', logPrefix, ': User manually changed level to:', newLevel);
    userChangedLevelRef.current = true;
    setSelectedLevel(newLevel);

    if (newLevel === 'Overall') {
      console.log('🔗', logPrefix, ': Switching to Overall, clearing URL params');
      updateUrlParams({
        "subcourse-id": null,
        "lesson-id": null,
        "skill-id": null,
      });
    } else if (courseTree) {
      const subcourse = findSubcourseByLevel(courseTree, newLevel);
      const subcourseId = subcourse?.id || subcourse?._id;
      if (subcourseId) {
        console.log('🔗', logPrefix, ': Updating URL params for level:', newLevel, 'subcourse-id:', subcourseId);
        updateUrlParams({
          "subcourse-id": subcourseId,
          "lesson-id": null,
          "skill-id": null,
        });
      }
    }
  };

  useEffect(() => {
    const fetchCourseTree = async () => {
      setLoading(true);
      const result = await getCourseTree();
      if (result.success && result.data) {
        let filteredData = result.data;

        if (result.data.language && Array.isArray(result.data.language)) {
          const course = result.data.language.find(c =>
            c['lang-code'] === langCode && c['published?'] === true
          );

          if (course) {
            filteredData = {
              courses: [course]
            };
          } else {
            console.warn('⚠️ No published course found for lang-code:', langCode);
          }
        }

        setCourseTree(filteredData);
        setCourseTreeAtom(filteredData);

        const existingBaseline = localStorage.getItem('course-tree-baseline');
        if (!existingBaseline) {
          try {
            localStorage.setItem('course-tree-baseline', JSON.stringify(filteredData));
            setCourseTreeBaseline(filteredData);
          } catch (e) {
            console.warn('Failed to save course-tree baseline:', e);
          }
        }
      }
      setLoading(false);
    };

    fetchCourseTree();
  }, [setCourseTreeAtom, setCourseTreeBaseline, langCode]);

  const handleQuickPracticeStart = (params) => {
    navigate('/quick-practice', { state: { ...params, fromPage } });
  };

  const handleWritingPracticeStart = (params) => {
    navigate('/writing-practice', { state: { ...params, fromPage } });
  };

  const handleSpeakingPracticeStart = (params) => {
    navigate('/speaking-practice', { state: { ...params, fromPage } });
  };

  const handleDrillVocabStart = (params) => {
    navigate('/drill-vocab', { state: { ...params, fromPage } });
  };

  const renderPanel = () => {
    console.log('🎨 renderPanel: Rendering panel for level:', selectedLevel, 'courseTree:', !!courseTree);

    if (!selectedLevel) {
      console.log('⏳ renderPanel: selectedLevel is null, showing loading');
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-6 text-[var(--text-secondary)]">
          <CircleNotch size={24} weight="bold" className="animate-spin" />
          <span className="text-[14px] text-[var(--text-secondary)]">Loading lessons...</span>
        </div>
      );
    }

    if (selectedLevel === 'Overall') {
      console.log('📊 renderPanel: Rendering OverallPanel');
      return <OverallPanel courseTree={courseTree} onQuickPracticeStart={handleQuickPracticeStart} onWritingPracticeStart={handleWritingPracticeStart} onSpeakingPracticeStart={handleSpeakingPracticeStart} />;
    }

    console.log('📚 renderPanel: Rendering LevelPanel for level:', selectedLevel);
    return <LevelPanel level={selectedLevel} courseTree={courseTree} loading={loading} onQuickPracticeStart={handleQuickPracticeStart} onWritingPracticeStart={handleWritingPracticeStart} onSpeakingPracticeStart={handleSpeakingPracticeStart} onDrillVocabStart={handleDrillVocabStart} />;
  };

  return (
    <Layout>
      <div className="language-learning-container w-full min-h-screen bg-[var(--bg-primary)] m-0 p-0 box-border max-md:min-h-[100dvh] max-md:h-auto max-md:overflow-visible max-md:flex max-md:flex-col">
        <Header selectedLevel={selectedLevel} setSelectedLevel={handleLevelChange} courseTree={courseTree} config={config} />

        <div className="panel-container w-full max-w-[1400px] mx-auto p-6 max-md:p-0 max-md:overflow-visible">
          {renderPanel()}
        </div>
      </div>
    </Layout>
  );
};

export default LanguageLearningPage;
